import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const WhopCallback = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState("Processing your Whop login...");

  useEffect(() => {
    const handleCallback = async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");

      if (!code) {
        toast.error("No authorization code received from Whop.");
        navigate("/auth");
        return;
      }

      try {
        setStatus("Verifying your Whop account...");

        const { data, error } = await supabase.functions.invoke("whop-auth", {
          body: { 
            code, 
            redirect_uri: `${window.location.origin}/auth/whop/callback` 
          },
        });

        if (error || data?.error) {
          throw new Error(data?.error || error?.message || "Whop authentication failed");
        }

        setStatus("Signing you in...");

        // Sign in using the session info returned
        if (data.session?.type === "new_user" || data.session?.type === "magiclink") {
          // Sign in with email/password for new users
          if (data.session.temp_password) {
            const { error: signInError } = await supabase.auth.signInWithPassword({
              email: data.session.email,
              password: data.session.temp_password,
            });
            if (signInError) {
              throw new Error(signInError.message);
            }
          } else {
            // For existing users, use OTP verification
            const { error: otpError } = await supabase.auth.verifyOtp({
              token_hash: data.session.token_hash,
              type: "magiclink",
            });
            if (otpError) {
              throw new Error(otpError.message);
            }
          }
        }

        toast.success(`Welcome${data.whop_username ? `, ${data.whop_username}` : ""}! Signed in via Whop.`);
        
        // Redirect based on plan
        if (data.plan_tier) {
          navigate("/app/dashboard");
        } else {
          navigate("/checkout");
        }
      } catch (err: any) {
        console.error("Whop callback error:", err);
        toast.error(err.message || "Failed to sign in with Whop");
        navigate("/auth");
      }
    };

    handleCallback();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center space-y-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
        <p className="text-muted-foreground">{status}</p>
      </div>
    </div>
  );
};

export default WhopCallback;
